/**
 * Process-wide singleton `IntersectionObserver`(s), keyed by `rootMargin`.
 *
 * The lazy-mount primitive (`LazyMount`) needs to know when an embedded board's
 * slot scrolls into view so it can mount the real board only then — the "only
 * render what's visible" half of hundreds-of-boards-per-page. A per-slot
 * `new IntersectionObserver` would be one native observer per board (the same
 * O(N) allocation the shared `ResizeObserver` avoids). Instead one observer per
 * distinct `rootMargin` watches every registered slot and fans each entry out
 * to the callbacks for that element.
 *
 * `root` is left null (the viewport). `IntersectionObserver` already clips the
 * target against every intermediate scroll container up to the root, so a slot
 * inside an `overflow:auto` catalog grid is correctly reported as off-screen
 * when scrolled out of that grid — no per-scroll-container observer needed.
 *
 * The observer delivers an initial entry for each newly-observed target, so a
 * slot already in view mounts on the first callback (no scroll required).
 */
type IntersectCb = (entry: IntersectionObserverEntry) => void;

interface Bucket {
  readonly io: IntersectionObserver;
  readonly registry: WeakMap<Element, Set<IntersectCb>>;
}

/** rootMargin → its observer. `let` only so the test reset can swap a fresh map. */
let buckets = new Map<string, Bucket>();

function ensureBucket(rootMargin: string): Bucket | null {
  if (typeof IntersectionObserver === "undefined") return null;
  const existing = buckets.get(rootMargin);
  if (existing) return existing;
  const registry = new WeakMap<Element, Set<IntersectCb>>();
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const cbs = registry.get(entry.target);
        if (!cbs) continue;
        // Snapshot: a callback may dispose itself (or a sibling) mid-dispatch —
        // a slot that mounts a board whose own effects unobserve a neighbour.
        for (const cb of [...cbs]) cb(entry);
      }
    },
    { rootMargin, threshold: 0 },
  );
  const bucket: Bucket = { io, registry };
  buckets.set(rootMargin, bucket);
  return bucket;
}

/**
 * Observe `el`'s intersection with the viewport (expanded by `rootMargin`),
 * invoking `cb` on each change. Returns a disposer that removes THIS callback;
 * the element is unobserved when its last callback goes. No-ops (disposer too)
 * where `IntersectionObserver` is absent (SSR / jsdom without the polyfill) —
 * `LazyMount` treats that as "mount eagerly" so content never vanishes.
 *
 * The FIRST callback for an element triggers `observe(el)`, so it receives the
 * initial entry `IntersectionObserver` delivers for a newly-observed target.
 */
export function observeIntersection(
  el: Element,
  cb: IntersectCb,
  opts?: { readonly rootMargin?: string },
): () => void {
  const bucket = ensureBucket(opts?.rootMargin ?? "0px");
  if (!bucket) return () => {};
  let cbs = bucket.registry.get(el);
  const isNew = !cbs;
  if (!cbs) {
    cbs = new Set();
    bucket.registry.set(el, cbs);
  }
  // Register BEFORE observing so a synchronous mount-fire (real IO fires async,
  // but a test double may fire inside observe()) already sees this callback.
  cbs.add(cb);
  if (isNew) bucket.io.observe(el); // first callback for this element → observe once
  let disposed = false;
  return () => {
    if (disposed) return; // idempotent: React may run cleanup more than once
    disposed = true;
    const set = bucket.registry.get(el);
    if (!set) return;
    set.delete(cb);
    if (set.size === 0) {
      bucket.registry.delete(el);
      bucket.io.unobserve(el);
    }
  };
}

/** Test-only: drop the singletons so a fresh fake `IntersectionObserver`
 *  installed by a test is the one used. Never called by app code. */
export function __resetSharedIntersectionObserverForTest(): void {
  buckets = new Map();
}
